'use client'

import { Suspense, useState } from 'react'
import { signIn } from 'next-auth/react'
import { useRouter, useSearchParams } from 'next/navigation'
import Link from 'next/link'
import { Button } from '@/components/ui/button'
import { useToast } from '@/components/ui/use-toast'
import { Loader2 } from 'lucide-react'
import {
  acceptGuestTokenAfterSignIn,
  buildAuthCallbackUrl,
} from '@/components/guest-invite-auth-handler'

function SignInForm() {
  const router = useRouter()
  const searchParams = useSearchParams()
  const { toast } = useToast()

  const guestToken = searchParams.get('guestToken')
  const emailParam = searchParams.get('email')
  const callbackUrl =
    searchParams.get('callbackUrl') ||
    buildAuthCallbackUrl(guestToken, emailParam)

  const [email, setEmail] = useState(emailParam || '')
  const [password, setPassword] = useState('')
  const [loading, setLoading] = useState(false)

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setLoading(true)

    try {
      const result = await signIn('credentials', {
        email,
        password,
        redirect: false,
        callbackUrl,
      } as any) as { error?: string; ok?: boolean; url?: string } | undefined

      if (result?.error) {
        toast({
          title: 'Error',
          description: result.error === 'CredentialsSignin'
            ? 'Invalid email or password'
            : result.error,
          variant: 'destructive',
        })
        setLoading(false)
      } else if (result?.ok || result?.url) {
        toast({
          title: 'Sign in successful',
          description: 'Redirecting...',
        })
        if (guestToken) {
          const slug = await acceptGuestTokenAfterSignIn(guestToken)
          if (slug) {
            window.location.href = `/${slug}`
            return
          }
        }

        const redirectUrl = result?.url
          ? new URL(result.url, window.location.origin).pathname
          : callbackUrl
        setTimeout(() => {
          router.push(redirectUrl)
        }, 100)
      } else {
        // If no error but also no success, the sign-in might have succeeded
        // Let NextAuth handle the redirect by using redirect: true
        console.log('Sign in result:', result)
        toast({
          title: 'Sign in successful',
          description: 'Redirecting...',
        })
        // Try using NextAuth's built-in redirect
        await signIn('credentials', {
          email,
          password,
          redirect: true,
          callbackUrl,
        } as any)
      }
    } catch (error: any) {
      console.error('Sign in error:', error)
      const errorMessage = error instanceof Error
        ? error.message
        : typeof error === 'string'
          ? error
          : 'An error occurred during sign in'
      toast({
        title: 'Error',
        description: errorMessage,
        variant: 'destructive',
      })
      setLoading(false)
    }
  }

  return (
    <div className="min-h-screen flex items-center justify-center p-4">
      <div className="w-full max-w-md">
        <h1 className="text-3xl font-bold mb-8 text-center">Sign In</h1>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="block text-sm font-medium mb-2">Email</label>
            <input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className="w-full px-4 py-2 border rounded-md bg-background text-foreground"
              required
            />
          </div>
          <div>
            <label className="block text-sm font-medium mb-2">Password</label>
            <input
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              className="w-full px-4 py-2 border rounded-md bg-background text-foreground"
              required
            />
          </div>
          <Button type="submit" className="w-full" disabled={loading}>
            {loading ? 'Signing in...' : 'Sign In'}
          </Button>
        </form>

        {/* Divider */}
        <div className="relative my-6">
          <div className="absolute inset-0 flex items-center">
            <span className="w-full border-t" />
          </div>
          <div className="relative flex justify-center text-xs uppercase">
            <span className="bg-background px-2 text-muted-foreground">Or continue with</span>
          </div>
        </div>

        {/* Google Sign In */}
        <Button
          type="button"
          variant="outline"
          className="w-full"
          onClick={() => signIn('google', { callbackUrl })}
        >
          <svg className="mr-2 h-4 w-4" viewBox="0 0 24 24">
            <path
              d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
              fill="#4285F4"
            />
            <path
              d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
              fill="#34A853"
            />
            <path
              d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
              fill="#FBBC05"
            />
            <path
              d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
              fill="#EA4335"
            />
          </svg>
          Sign in with Google
        </Button>

        <div className="mt-4 text-center">
          <Button
            type="button"
            variant="link"
            onClick={async (e) => {
              e.preventDefault()
              e.stopPropagation()

              if (!email) {
                toast({
                  title: 'Error',
                  description: 'Please enter your email address first',
                  variant: 'destructive',
                })
                return
              }
              try {
                const result = await signIn('email', {
                  email,
                  redirect: false,
                  callbackUrl,
                } as any) as { error?: string; ok?: boolean; url?: string } | undefined

                if (result?.error) {
                  if (result.error.includes('Email') || result.error.includes('SMTP')) {
                    toast({
                      title: 'Magic link not available',
                      description: 'Email authentication is not configured. Please use password sign in.',
                      variant: 'destructive',
                    })
                  } else {
                    toast({
                      title: 'Error',
                      description: result.error,
                      variant: 'destructive',
                    })
                  }
                } else {
                  toast({
                    title: 'Check your email',
                    description: 'We sent you a magic link to sign in.',
                  })
                  router.push('/auth/verify')
                }
              } catch (error: any) {
                console.error('Magic link error:', error)
                const errorMessage = error instanceof Error
                  ? error.message
                  : typeof error === 'string'
                    ? error
                    : 'Failed to send magic link. Email authentication may not be configured.'
                toast({
                  title: 'Error',
                  description: errorMessage,
                  variant: 'destructive',
                })
              }
            }}
          >
            Forgotten password? Sign in with magic link
          </Button>
        </div>
        <div className="mt-4 text-center space-x-4">
          <Link href="/docs" className="text-xs text-muted-foreground hover:text-foreground transition-colors">
            Help
          </Link>
          <span className="text-xs text-muted-foreground">•</span>
          <Link href="/terms" className="text-xs text-muted-foreground hover:text-foreground transition-colors">
            Terms of Use
          </Link>
        </div>
      </div>
    </div>
  )
}

export default function SignInPage() {
  return (
    <Suspense
      fallback={
        <div className="min-h-screen flex items-center justify-center">
          <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
        </div>
      }
    >
      <SignInForm />
    </Suspense>
  )
}
